import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Baby, CalendarDays, Heart } from "lucide-react";
import { motion } from "framer-motion";

const trimesterMeta = (week) => {
  if (week <= 13) return { label: "First Trimester", color: "text-emerald-300", bg: "bg-emerald-500/15", border: "border-emerald-500/30" };
  if (week <= 27) return { label: "Second Trimester", color: "text-sky-300", bg: "bg-sky-500/15", border: "border-sky-500/30" };
  return { label: "Third Trimester", color: "text-rose-300", bg: "bg-rose-500/15", border: "border-rose-500/30" };
};

const weeklyTip = (week) => {
  if (week <= 13) return "Focus on folate, gentle movement, and extra rest. First-trimester fatigue is real.";
  if (week <= 20) return "Energy is returning — great time for prenatal yoga and journaling your birth vision.";
  if (week <= 27) return "Baby is growing quickly. Iron-rich foods and left-side sleeping help circulation.";
  if (week <= 36) return "Nesting energy rises. Pack your hospital bag, pre-register, and rest often.";
  return "Full term approaching. Short daily walks, birth affirmations, and patience — you're close.";
};

const PregnancyWeekCard = ({ startDate, dueDate }) => {
  if (!startDate && !dueDate) return null;

  let week = 1;
  let daysIntoWeek = 0;
  let daysRemaining = null;

  const today = new Date();
  if (dueDate) {
    const due = new Date(dueDate);
    // LMP = due date - 280 days
    const lmp = new Date(due.getTime() - 280 * 24 * 60 * 60 * 1000);
    const diffDays = Math.floor((today - lmp) / (1000 * 60 * 60 * 24));
    week = Math.max(1, Math.min(42, Math.floor(diffDays / 7) + 1));
    daysIntoWeek = diffDays % 7;
    daysRemaining = Math.max(0, Math.floor((due - today) / (1000 * 60 * 60 * 24)));
  } else if (startDate) {
    const start = new Date(startDate);
    const diffDays = Math.floor((today - start) / (1000 * 60 * 60 * 24));
    week = Math.max(1, Math.min(42, Math.floor(diffDays / 7) + 1));
    daysIntoWeek = diffDays % 7;
  }

  const tri = trimesterMeta(week);
  const progressPct = Math.min(100, Math.round((week / 40) * 100));

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.18 }}
      data-testid="pregnancy-week-card"
    >
      <Card className={`glass ${tri.border} bg-gradient-to-br from-pink-500/10 via-purple-500/5 to-transparent`}>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3">
            <CardTitle className="text-white flex items-center gap-2">
              <Baby className="w-5 h-5 text-pink-300" />
              Pregnancy Journey
            </CardTitle>
            <span className={`text-xs px-3 py-1 rounded-full border ${tri.bg} ${tri.color} ${tri.border}`} data-testid="trimester-badge">
              {tri.label}
            </span>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-end justify-between">
            <div>
              <p className="text-pink-200 text-xs uppercase tracking-wider mb-1">Current Week</p>
              <p className="text-white text-3xl font-bold" data-testid="pregnancy-week-number">
                Week {week}
                <span className="text-lg text-mystic-200 font-normal ml-2">of 40</span>
              </p>
              <p className="text-mystic-200/70 text-xs mt-1">Day {daysIntoWeek} of current week</p>
            </div>
            {daysRemaining !== null && (
              <div className="text-right">
                <p className="text-pink-200 text-xs uppercase tracking-wider mb-1 flex items-center gap-1 justify-end">
                  <CalendarDays className="w-3 h-3" /> Due
                </p>
                <p className="text-white text-lg font-semibold" data-testid="days-remaining">
                  {daysRemaining === 0 ? "Today!" : `${daysRemaining} days`}
                </p>
                <p className="text-mystic-200/70 text-xs">{dueDate}</p>
              </div>
            )}
          </div>

          <Progress value={progressPct} className="h-2" data-testid="pregnancy-progress" />

          <div className="rounded-xl bg-mystic-900/40 p-3 border border-white/5 flex items-start gap-2">
            <Heart className="w-4 h-4 text-pink-300 flex-shrink-0 mt-0.5" />
            <p className="text-mystic-100 text-sm leading-relaxed" data-testid="pregnancy-weekly-tip">
              {weeklyTip(week)}
            </p>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default PregnancyWeekCard;
